/**
 * Workshop Service
 * Handles all workshop scheduling API calls
 */

import axios from 'axios';

// Use the same backend URL detection logic as api.js (Flask blueprints are under /api, e.g. /api/workshops)
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? window.location.protocol + '//' + window.location.hostname + ':5001/api'
  : window.location.protocol + '//' + window.location.hostname + '/api';

console.log('Workshop Service - Using backend URL:', API_BASE_URL);

// Configure axios instance with credentials for authentication
axios.defaults.withCredentials = true;

// ==================== Workshop Settings ====================

export const getWorkshopSettings = async (projectId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/workshops/settings/${projectId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No settings found
    }
    throw error;
  }
};

export const saveWorkshopSettings = async (projectId, settings) => {
  const response = await axios.post(`${API_BASE_URL}/workshops/settings/${projectId}`, settings);
  return response.data;
};

// ==================== Scope Items ====================

export const uploadScopeItemsExcel = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/workshops/scope-items/${projectId}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const downloadScopeItemTemplate = () => {
  const url = `${API_BASE_URL}/workshops/scope-items/template/download`;
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Scope_Item_Template.xlsx';
  a.click();
};

export const getScopeItems = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/scope-items/${projectId}`);
  return response.data;
};

export const updateScopeItem = async (itemId, data) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/scope-items/${itemId}`, data);
  return response.data;
};

export const deleteScopeItem = async (itemId) => {
  const response = await axios.delete(`${API_BASE_URL}/workshops/scope-items/${itemId}`);
  return response.data;
};

export const clearAllScopeItems = async (projectId) => {
  const response = await axios.delete(`${API_BASE_URL}/workshops/scope-items/${projectId}/clear-all`);
  return response.data;
};

export const replaceDuplicateScopeItems = async (projectId, replacements) => {
  const response = await axios.post(
    `${API_BASE_URL}/workshops/scope-items/${projectId}/replace-duplicates`,
    { replacements }
  );
  return response.data;
};

// ==================== Workshop Names ====================

export const getWorkshopNames = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/names/${projectId}`);
  return response.data;
};

export const createWorkshopName = async (projectId, workshopName) => {
  const response = await axios.post(`${API_BASE_URL}/workshops/names/${projectId}`, {
    workshop_name: workshopName
  });
  return response.data;
};

export const autoGenerateWorkshops = async (projectId) => {
  const response = await axios.post(`${API_BASE_URL}/workshops/names/${projectId}/auto-generate`);
  return response.data;
};

export const updateWorkshopName = async (workshopNameId, newName) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/names/${workshopNameId}`, {
    workshop_name: newName
  });
  return response.data;
};

export const deleteWorkshopName = async (workshopNameId) => {
  const response = await axios.delete(`${API_BASE_URL}/workshops/names/${workshopNameId}`);
  return response.data;
};

export const moveWorkshopUp = async (workshopNameId) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/names/${workshopNameId}/move-up`);
  return response.data;
};

export const moveWorkshopDown = async (workshopNameId) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/names/${workshopNameId}/move-down`);
  return response.data;
};

// ==================== Scope Mappings ====================

export const getScopeMappings = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/mappings/${projectId}`);
  return response.data;
};

export const createScopeMapping = async (workshopNameId, scopeItemId) => {
  const response = await axios.post(`${API_BASE_URL}/workshops/mappings`, {
    workshop_name_id: workshopNameId,
    scope_item_id: scopeItemId
  });
  return response.data;
};

export const updateScopeMapping = async (scopeItemId, workshopNameId) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/mappings/scope-item/${scopeItemId}`, {
    workshop_name_id: workshopNameId
  });
  return response.data;
};

export const deleteScopeMapping = async (mappingId) => {
  const response = await axios.delete(`${API_BASE_URL}/workshops/mappings/${mappingId}`);
  return response.data;
};

// ==================== Module Attendees ====================

export const getModuleAttendees = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/attendees/${projectId}`);
  return response.data;
};

export const addModuleAttendee = async (projectId, module, attendeeEmail, attendeeName, isMandatory = false) => {
  const response = await axios.post(`${API_BASE_URL}/workshops/attendees/${projectId}`, {
    module,
    attendee_email: attendeeEmail,
    attendee_name: attendeeName,
    is_mandatory: isMandatory
  });
  return response.data;
};

export const updateModuleAttendee = async (attendeeId, data) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/attendees/${attendeeId}`, data);
  return response.data;
};

export const deleteModuleAttendee = async (attendeeId) => {
  const response = await axios.delete(`${API_BASE_URL}/workshops/attendees/${attendeeId}`);
  return response.data;
};

export const syncStakeholdersToWorkshopModules = async (projectId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/projects/${projectId}/stakeholders/sync-workshop-modules`);
    return response.data;
  } catch (error) {
    // Non-fatal — silently ignore if sync fails
    return { success: false };
  }
};


// ==================== Workshops ====================

export const getWorkshops = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/${projectId}`);
  return response.data;
};

export const getWorkshop = async (workshopId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/${workshopId}`);
  return response.data;
};

export const updateWorkshop = async (workshopId, data) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/workshop/${workshopId}`, data);
  return response.data;
};

// ==================== AI Generation ====================

export const generateWorkshops = async (projectId) => {
  // Set a longer timeout for workshop generation (5 minutes) since it makes multiple OpenAI API calls
  const response = await axios.post(
    `${API_BASE_URL}/workshops/generate/${projectId}`,
    {},
    { timeout: 300000 } // 5 minutes
  );
  return response.data;
};

export const generateSchedule = async (projectId) => {
  // Set a longer timeout for schedule generation (5 minutes)
  const response = await axios.post(
    `${API_BASE_URL}/workshops/schedule/${projectId}`,
    {},
    { timeout: 300000 } // 5 minutes
  );
  return response.data;
};

// ==================== Workshop Schedules ====================

export const getWorkshopSchedules = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/schedules/${projectId}`);
  return response.data;
};

export const updateWorkshopSchedule = async (scheduleId, data) => {
  const response = await axios.put(`${API_BASE_URL}/workshops/schedules/${scheduleId}`, data);
  return response.data;
};

export const deleteWorkshopSchedule = async (scheduleId) => {
  const response = await axios.delete(`${API_BASE_URL}/workshops/schedules/${scheduleId}`);
  return response.data;
};

export const createManualSchedule = async (data) => {
  const response = await axios.post(`${API_BASE_URL}/workshops/schedules/manual`, data);
  return response.data;
};

// ==================== Schedule Export/Import ====================

export const downloadScheduleExcel = async (projectId) => {
  const response = await axios.get(
    `${API_BASE_URL}/workshops/schedules/${projectId}/export`,
    { responseType: 'blob' }
  );

  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;

  // Extract filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `Workshop_Schedule_${projectId}.xlsx`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

export const previewScheduleChanges = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/workshops/schedules/${projectId}/preview`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const uploadScheduleExcel = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/workshops/schedules/${projectId}/import`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ==================== Calendar Invites ====================

export const getCalendarInvites = async (scheduleId) => {
  const response = await axios.get(`${API_BASE_URL}/workshops/calendar-invites/${scheduleId}`);
  return response.data;
};

export const generateCalendarInvite = async (scheduleId) => {
  const response = await axios.post(
    `${API_BASE_URL}/workshops/calendar-invites/generate/${scheduleId}`,
    {},
    { timeout: 300000 } // 5 minutes
  );
  return response.data;
};

export const generateAllCalendarInvites = async (projectId) => {
  const response = await axios.post(
    `${API_BASE_URL}/workshops/calendar-invites/generate-all/${projectId}`,
    {},
    { timeout: 300000 } // 5 minutes
  );
  return response.data;
};

// ==================== Helper Functions ====================

export const getDefaultWorkshopSettings = () => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() + 7); // Start 1 week from now

  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + 3); // 3 months duration

  return {
    time_zone: 'PST',
    start_date: startDate.toISOString().split('T')[0],
    end_date: endDate.toISOString().split('T')[0],
    allowed_start_time: '08:00:00',
    allowed_end_time: '18:00:00',
    duration_hours: 2.0,
    weekends_allowed: false,
    exception_dates: [],
    exact_workshops_required: null,
    min_workshops_required: 1,
    max_workshops_required: 20,
    allow_multiple_per_day: false,
    min_workshops_per_day: null,
    max_workshops_per_day: null
  };
};

export const downloadICSFile = (icsData, workshopName) => {
  const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${workshopName.replace(/\s+/g, '_')}.ics`;
  link.click();
};

export const formatWorkshopDate = (dateString) => {
  if (!dateString) return '';
  // Parse parts manually: new Date("YYYY-MM-DD") is UTC midnight, which shifts
  // the day backward for negative-offset (US) timezones causing a 1-day diff.
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day); // local midnight, no UTC shift
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatWorkshopTime = (timeString) => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export const getTimezoneName = (timezoneCode) => {
  const timezones = {
    'PST': 'Pacific Time',
    'EST': 'Eastern Time',
    'CST': 'Central Time',
    'MST': 'Mountain Time',
    'UTC': 'UTC',
    'GMT': 'GMT'
  };
  return timezones[timezoneCode] || timezoneCode;
};

export const timezoneOptions = [
  { value: 'PST', label: 'Pacific Time (PST)' },
  { value: 'EST', label: 'Eastern Time (EST)' },
  { value: 'CST', label: 'Central Time (CST)' },
  { value: 'MST', label: 'Mountain Time (MST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'GMT', label: 'GMT' }
];

// ==================== Stakeholders (for attendee picker) ====================

export const getProjectStakeholdersForPicker = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/projects/${projectId}/stakeholders`);
  return response.data.stakeholders || [];
};
